import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Table, Spinner, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { useAlertModal } from '../../AlertModalContext';

const WHATSAPP_MESSAGE_LIMIT = 300;

const CommunicationModal = ({
  show,
  onHide,
  appointments,
  pacientesList,
  todosMedicos,
  especialidades,
  currentEntidade,
  currentUser,
  onSendComplete,
  mode = 'agenda', // Novo prop: 'agenda' ou 'comunicado'
}) => {
  const { showAlertModal } = useAlertModal();

  // --- Estados Internos do Modal ---
  const [communicationMethod, setCommunicationMethod] = useState('email');
  const [whatsAppMessage, setWhatsAppMessage] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [selectedPatients, setSelectedPatients] = useState({});
  const [selectedRecipient, setSelectedRecipient] = useState('todos');
  const [selectAll, setSelectAll] = useState(false);
  const [isLoadingBulk, setIsLoadingBulk] = useState(false);

  // Memoiza a lista de agendamentos que podem receber comunicação
  const listItems = useMemo(() => {
    if (mode === 'comunicado') {
      // No modo comunicado, a lista são todos os pacientes
      return (pacientesList || []).map(p => ({
        id: p.id, // Usamos o ID do paciente como chave
        paciente: p, // O objeto paciente completo
        // Valores placeholder para colunas que não se aplicam
        hora_marcada: '--:--',
        tipo: 'Comunicado',
        status: 'Cadastrado',
      }));
    }
    // Modo padrão 'agenda'
    return (appointments || [])
      .filter(agendamento => agendamento.status === 'Agendada');
  }, [mode, appointments, pacientesList]);

  // Efeito para resetar o estado interno quando o modal é aberto
  useEffect(() => {
    if (show) {
      const isAgendaMode = mode === 'agenda';
      const defaultMessage = isAgendaMode ? `Olá, {paciente_nome}! Lembrete do seu agendamento de {especialidade_nome} com Dr(a). {medico_nome} para o dia {data_agendamento} às {hora_marcada}.` : `Olá, {paciente_nome}! Temos um comunicado importante da ${currentEntidade?.nome || 'Clínica'}.`;
      const defaultEmailSubject = isAgendaMode ? `Lembrete de Agendamento - ${currentEntidade?.nome || 'Clínica'}` : `Comunicado Importante - ${currentEntidade?.nome || 'Clínica'}`;
      const defaultEmailBody = isAgendaMode ? `Olá, {paciente_nome}!\n\nEste é um lembrete do seu agendamento de {especialidade_nome} com Dr(a). {medico_nome} para o dia {data_agendamento} às {hora_marcada}.\n\nAtenciosamente,\nEquipe ${currentEntidade?.nome || 'da Clínica'}.` : `Olá, {paciente_nome}!\n\nEste é um comunicado importante da ${currentEntidade?.nome || 'Clínica'}.\n\n[Insira seu comunicado aqui]\n\nAtenciosamente,\nEquipe ${currentEntidade?.nome || 'da Clínica'}.`;

      setWhatsAppMessage(defaultMessage);
      setEmailSubject(defaultEmailSubject);
      setEmailMessage(defaultEmailBody);
      setSelectedPatients({});
      setSelectedRecipient('todos');
      setSelectAll(false);
      setCommunicationMethod('email');
    }
  }, [show, currentEntidade, mode]);

  // --- Funções de Lógica Interna (movidas de AgendasTab.js) ---

  const handleCheckboxChange = (appointmentId) => {
    setSelectedPatients(prev => ({ ...prev, [appointmentId]: !prev[appointmentId] }));
  };

  const handleRecipientChange = (event) => {
    const value = event.target.value;
    setSelectedRecipient(value);
    if (value === 'todos') {
      setSelectedPatients({});
      setSelectAll(false);
    }
  };

  const handleSelectAllChange = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    const newSelected = {};
    if (newSelectAll) {
      listItems.forEach(item => { newSelected[item.id] = true; });
    }
    setSelectedPatients(newSelected);
  };

  const getAgendamentosParaComunicacao = (checkMensagemEnviada = false) => {
    let possibleItems = listItems;
    if (mode === 'agenda') {
      if (checkMensagemEnviada) {
        // Para e-mails, evita reenvio se já foi enviado.
        // No modo comunicado, essa verificação não se aplica.
        possibleItems = possibleItems.filter(
          (agendamento) => agendamento.mensagem === null || agendamento.mensagem === 'enviado'
        );
      }
    }
    if (selectedRecipient === 'todos') return possibleItems;
    
    const selectedIds = Object.keys(selectedPatients).filter(id => selectedPatients[id]).map(id => parseInt(id, 10));
    return possibleItems.filter(item => selectedIds.includes(item.id));
  };

  const handleEnviarWhatsApp = async () => {
    if (!currentEntidade) {
        showAlertModal('Erro', 'Entidade não carregada.', 'danger');
        return;
    }
    if (!whatsAppMessage.trim()) {
        showAlertModal('Atenção', 'A mensagem de WhatsApp não pode estar vazia.', 'warning');
        return;
    }
  
    const agendamentosToSend = getAgendamentosParaComunicacao(false);
  
    if (agendamentosToSend.length === 0) {
        showAlertModal('Atenção', 'Nenhum destinatário válido selecionado para envio.', 'warning');
        return;
    }

    const messagesPayload = agendamentosToSend.map(item => {
        let paciente;
        if (mode === 'comunicado') {
            // No modo 'comunicado', o item já contém o objeto paciente completo.
            paciente = item.paciente;
        } else { // mode === 'agenda'
            // No modo 'agenda', o item é um agendamento e buscamos o paciente na lista completa.
            paciente = pacientesList.find(p => p.id === item.paciente_id);
        }
        
        if (!paciente || !paciente.dddcelular || !paciente.celular) {
            console.warn(`Paciente ${paciente?.nome || `com ID ${item.id}`} sem número de telefone válido. Pulando.`);
            return null;
        }

        const telefone = `55${paciente.dddcelular}${paciente.celular}`;
        let personalizedMessage = whatsAppMessage.replace(/{paciente_nome}/g, paciente.nome);

        if (mode === 'agenda') {
          const medico = todosMedicos.find(m => m.id === item.medico_id);
          const especialidade = especialidades.find(e => e.id === item.especialidade_id);
          personalizedMessage = personalizedMessage
            .replace(/{data_agendamento}/g, new Date(item.data_agendamento + 'T00:00:00').toLocaleDateString('pt-BR'))
            .replace(/{hora_marcada}/g, item.hora_marcada.slice(0, 5))
            .replace(/{medico_nome}/g, medico ? medico.nome : 'N/A')
            .replace(/{especialidade_nome}/g, especialidade ? especialidade.nome : 'N/A');
        }

        // A API de WhatsApp pode ou não precisar do agendamento_id, mas é bom ter.
        return { to: telefone, body: personalizedMessage, agendamento_id: mode === 'agenda' ? item.id : null };
    }).filter(Boolean);

    if (messagesPayload.length === 0) {
        showAlertModal('Atenção', 'Nenhum dos pacientes selecionados possui um número de telefone válido.', 'warning');
        return;
    }

    await new Promise(resolve => setTimeout(resolve, 1000)); // Simula delay da API

    showAlertModal('Sucesso', 'Mensagens de WhatsApp preparadas para envio pelo backend!', 'success');
  };

  const handleEnviarMensagens = async () => { // Refatorado para envio em lote
    if (!currentEntidade || !currentUser) {
      showAlertModal('Erro', 'Entidade ou usuário não carregado.', 'danger');
      return;
    }
    if (!emailSubject.trim() || !emailMessage.trim()) {
      showAlertModal('Atenção', 'O assunto e o corpo do e-mail não podem estar vazios.', 'warning');
      return;
    }

    const itemsToSend = getAgendamentosParaComunicacao(true);

    if (itemsToSend.length === 0) {
      showAlertModal('Atenção', 'Nenhum destinatário válido selecionado para envio de e-mail.', 'warning');
      return;
    }

    // Mapeia a lista de itens (agendamentos ou pacientes) para o formato de destinatário esperado pelo backend.
    const destinatariosPayload = itemsToSend.map(item => {
      let paciente;
      if (mode === 'comunicado') {
        // No modo 'comunicado', o item já contém o objeto paciente completo.
        paciente = item.paciente;
      } else { // mode === 'agenda'
        // No modo 'agenda', o item é um agendamento e buscamos o paciente na lista completa.
        paciente = pacientesList.find(p => p.id === item.paciente_id);
      }

      if (!paciente || !paciente.email) {
        console.warn(`Paciente ID ${paciente?.id || item.id} sem e-mail válido. Pulando.`);
        return null;
      }

      // Base do payload do destinatário
      const recipientData = {
        paciente_id: paciente.id,
        email: paciente.email,
        nome: paciente.nome, // O backend usará isso para substituir {paciente_nome}
        cpf: paciente.cpf, // O backend pode precisar para logs ou outras integrações
      };

      if (mode === 'agenda') {
        const medico = todosMedicos.find(m => m.id === item.medico_id);
        const especialidade = especialidades.find(e => e.id === item.especialidade_id);
        
        recipientData.agendamento_id = item.id; // Importante para o backend atualizar o status
        // Envia os dados para personalização no backend
        recipientData.data_agendamento = new Date(item.data_agendamento + 'T00:00:00').toLocaleDateString('pt-BR');
        recipientData.hora_marcada = item.hora_marcada.slice(0, 5);
        recipientData.medico_nome = medico ? medico.nome : 'N/A';
        recipientData.especialidade_nome = especialidade ? especialidade.nome : 'N/A';
      }

      return recipientData;
    }).filter(Boolean); // Remove os nulos (pacientes sem email)

    if (destinatariosPayload.length === 0) {
      showAlertModal('Atenção', 'Nenhum dos pacientes selecionados possui um endereço de e-mail válido.', 'warning');
      return;
    }

    // Monta o payload final para a chamada em lote
    const batchPayload = {
      entidade_id: currentEntidade.id,
      funcionario_id: currentUser.id,
      ds_assunto: emailSubject,
      ds_conteudo_template: emailMessage, // Envia o template. O backend fará a personalização.
      destinatarios: destinatariosPayload,
      mode: mode // Informa ao backend o contexto para a personalização das tags
    };

    // BACKEND: Este é o novo endpoint que você precisará criar.
    // Ele deve aceitar o payload acima e enfileirar os e-mails para envio.
    await axios.post('/api/mensagens/enviar-em-lote', batchPayload);

    showAlertModal('Sucesso', `${destinatariosPayload.length} e-mails foram colocados na fila de envio com sucesso!`, 'success');
  };

  const handleSendCommunication = async () => {
    setIsLoadingBulk(true);
    try {
      if (communicationMethod === 'email') {
        await handleEnviarMensagens();
      } else {
        await handleEnviarWhatsApp();
      }
      if (onSendComplete) {
        onSendComplete(); // Notifica o pai para re-buscar os dados
      }
      onHide(); // Fecha o modal
    } catch (error) {
      console.error("Erro ao despachar comunicação:", error);
      showAlertModal('Erro', `Falha no envio: ${error.message}`, 'danger');
    } finally {
      setIsLoadingBulk(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Comunicar Pacientes</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Método de Comunicação:</Form.Label>
          <div>
            <Form.Check
              inline
              type="radio"
              label="E-mail"
              name="communicationMethod"
              id="comm-method-email"
              value="email"
              checked={communicationMethod === 'email'}
              onChange={(e) => setCommunicationMethod(e.target.value)}
            />
            {/* <Form.Check
              inline
              type="radio"
              label="WhatsApp"
              name="communicationMethod"
              id="comm-method-whatsapp"
              value="whatsapp"
              checked={communicationMethod === 'whatsapp'}
              onChange={(e) => setCommunicationMethod(e.target.value)}
            /> */}
          </div>
        </Form.Group>

        {communicationMethod === 'email' && (
          <>
            <Form.Group controlId="emailSubject" className="mt-3">
              <Form.Label>Assunto do E-mail:</Form.Label>
              <Form.Control
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="emailMessage" className="mt-3">
              <Form.Label>Corpo do E-mail:</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
              />
              <Form.Text className="text-muted">
                {mode === 'agenda'
                  ? 'Use as mesmas tags do WhatsApp para personalizar a mensagem.'
                  : 'Use {paciente_nome} para personalizar a mensagem.'
                }

                </Form.Text>
            </Form.Group>
          </>
        )}

        {communicationMethod === 'whatsapp' && (
          <Form.Group controlId="whatsappMessage" className="mt-3">
            <Form.Label>Mensagem de WhatsApp:</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={whatsAppMessage}
              onChange={(e) => setWhatsAppMessage(e.target.value)}
              maxLength={WHATSAPP_MESSAGE_LIMIT}
            />
            <div className="d-flex justify-content-between">
              <Form.Text className="text-muted" style={{ maxWidth: '80%' }}>
                {mode === 'agenda'
                  ? `Use {paciente_nome}, {data_agendamento}, {hora_marcada}, {medico_nome} e {especialidade_nome}.`
                  : `Use {paciente_nome}.`
                }
              </Form.Text>
              <Form.Text className={whatsAppMessage.length >= WHATSAPP_MESSAGE_LIMIT ? 'text-danger' : 'text-muted'}>
                {whatsAppMessage.length}/{WHATSAPP_MESSAGE_LIMIT}
              </Form.Text>
            </div>
          </Form.Group>
        )}
        <hr />
        <Form.Group controlId="recipientSelect">
          <Form.Label>Destinatários:</Form.Label>
          <Form.Select aria-label="Seleção de destinatários" value={selectedRecipient} onChange={handleRecipientChange}>
            <option value="todos">{mode === 'agenda' ? 'Todos os Pacientes Agendados' : 'Todos os Pacientes Cadastrados'}</option>
            <option value="individual">Individual</option>
          </Form.Select>
        </Form.Group>
        <Table striped bordered hover className="mt-3">
          <thead>
            <tr>
              <th>
                <Form.Check
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAllChange}
                  disabled={selectedRecipient === 'todos'}
                />
              </th>
              <th>Hora</th>
              <th>Paciente</th>
              <th className="d-none d-md-table-cell">Tipo</th>
              <th className="d-none d-md-table-cell">Status</th>
            </tr>
          </thead>
          <tbody>
            {listItems.map((agendamento) => (
              <tr key={agendamento.id}>
                <td>
                  <Form.Check
                    type="checkbox"
                    checked={selectedRecipient === 'todos' || selectedPatients[agendamento.id] || false}
                    onChange={() => handleCheckboxChange(agendamento.id)}
                    disabled={selectedRecipient === 'todos'}
                  />
                </td>
                <td>{agendamento.hora_marcada.slice(0, 5)}</td>
                <td>{agendamento.paciente?.nome || 'Paciente não encontrado'}</td>
                <td className="d-none d-md-table-cell">{agendamento.tipo}</td>
                <td className="d-none d-md-table-cell">{agendamento.status}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isLoadingBulk}>Fechar</Button>
        <Button variant="primary" onClick={handleSendCommunication} disabled={isLoadingBulk}>
          {isLoadingBulk && <Spinner as="span" animation="border" size="sm" className="me-2" />}
          {isLoadingBulk ? 'Enviando...' : 'Confirmar Envio'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CommunicationModal;